import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TbPencilPlus, TbPencilX } from "react-icons/tb";
import { BiPencil } from "react-icons/bi";
import { useTranslation } from "react-i18next";
import type { AxiosInstance } from "axios";
import { U2000HandleApiError } from "../../axios/U2000HandleApiError";
import type { U2000ApiErrorResult } from "../../axios/U2000HandleApiError";
import useU2000AxiosAuthenticated from "../../axios/U2000UseAxiosAuthenticated";
import { FaAngleRight } from "react-icons/fa";
import { U2000FormatDateTime } from "../../tools/U2000DateTimeHelpers";
import { useToast } from "../../context/toast/U2000ToastContext";

// Types pour les audits
// Structure basée sur la nouvelle API : mainEntities contient des objets avec entityId, entityName, logicalUnits et parentEntityId
interface LogicalUnitAudit {
  entityId: string; // UUID de l'entité (requis)
  entityName: string; // Nom de l'entité (requis)
  action: string; // Action effectuée (requis)
  delta?: string; // Delta JSON (optionnel)
  parentEntityId?: string; // UUID de l'entité parente (optionnel)
  [key: string]: unknown; // Autres propriétés possibles
}

interface LogicalUnit {
  logicalUnitCode: string;
  audits?: LogicalUnitAudit[]; // Audits pour cette unité logique
  [key: string]: unknown; // Autres propriétés possibles
}

interface MainEntityType {
  entityId: string; // UUID de l'entité (requis)
  entityName: string; // Nom de l'entité (requis)
  logicalUnits?: LogicalUnit[]; // Unités logiques (optionnel)
  parentEntityId?: string; // UUID de l'entité parente (optionnel)
  action?: string; // Action effectuée (optionnel, rétrocompatibilité)
  delta?: string; // Delta JSON (optionnel, rétrocompatibilité)
  // Champs de rétrocompatibilité (optionnels)
  id?: string | number;
}

interface AuditType {
  requestUuid: string; // UUID de la requête (requis dans la nouvelle structure)
  createdAt: string; // Date de création (requis)
  username?: string; // Nom d'utilisateur (optionnel)
  mainEntities?: MainEntityType[]; // Entités principales (nouvelle structure)
  // Champs de rétrocompatibilité (optionnels)
  id?: string | number;
  audits?: MainEntityType[]; // Ancienne structure (rétrocompatibilité)
}

interface U2000AuditPanelComponentProps {
  id: string | number;
  doGetAuditsData: (axiosInstance: AxiosInstance, id: string | number) => Promise<{ data: { results: AuditType[] } }>;
  toastName?: string;
  height?: string | number;
  maxHeight?: string | number;
  className?: string;
}

// Fonctions utilitaires pures (extractées du composant pour meilleures performances)
function getAuditStyle(action: string | undefined) {
    if (!action || typeof action !== "string") {
      return {
        bg: "bg-gray-50",
        border: "border-gray-200",
        icon: <BiPencil className="h-5 w-5 text-gray-600" />,
        iconBg: "bg-gray-100",
      };
    }
    switch (action.toLowerCase()) {
      case "insert entity":
        return {
          bg: "bg-green-50",
          border: "border-green-200",
          icon: <TbPencilPlus className="h-5 w-5 text-green-600" />,
          iconBg: "bg-green-100",
        };
      case "update entity":
        return {
          bg: "bg-blue-50",
          border: "border-blue-200",
          icon: <BiPencil className="h-5 w-5 text-blue-600" />,
          iconBg: "bg-blue-100",
        };
      case "delete soft entity":
        return {
          bg: "bg-red-50",
          border: "border-red-200",
          icon: <TbPencilX className="h-5 w-5 text-red-600" />,
          iconBg: "bg-red-100",
        };
      default:
        return {
          bg: "bg-gray-50",
          border: "border-gray-200",
          icon: <BiPencil className="h-5 w-5 text-gray-600" />,
          iconBg: "bg-gray-100",
        };
    }
}

function getAuditPanelStyle(mainEntities: MainEntityType[] | undefined) {
    if (!mainEntities || !Array.isArray(mainEntities)) {
      return {
        bg: "bg-gray-50",
        border: "border-gray-300",
        accent: "border-l-4 border-l-gray-500",
      };
    }
    // Parcourir toutes les entités et leurs logicalUnits pour trouver les actions
    const allActions: string[] = [];
    mainEntities.forEach((entity) => {
      // Vérifier l'action directe de l'entité (rétrocompatibilité)
      if (entity.action) {
        allActions.push(entity.action);
      }
      // Vérifier les actions dans logicalUnits
      if (entity.logicalUnits) {
        entity.logicalUnits.forEach((logicalUnit) => {
          if (logicalUnit.audits) {
            logicalUnit.audits.forEach((audit) => {
              if (audit.action) {
                allActions.push(audit.action);
              }
            });
          }
        });
      }
    });
    const hasDelete = allActions.some((action) => typeof action === "string" && action.toLowerCase() === "delete soft entity");
    const hasModify = allActions.some((action) => action === "update entity" || action.toLowerCase() === "update entity");
    if (hasDelete) {
      return {
        bg: "bg-red-50",
        border: "border-red-300",
        accent: "border-l-4 border-l-red-500",
      };
    }
    if (hasModify) {
      return {
        bg: "bg-blue-50",
        border: "border-blue-300",
        accent: "border-l-4 border-l-blue-500",
      };
    }
    return {
      bg: "bg-gray-50",
      border: "border-gray-200",
      accent: "border-l-4 border-l-gray-400",
    };
}

// Composant SubAudit extrait pour meilleures performances
interface SubAuditProps {
  mainEntity: MainEntityType;
  uniqueId: string; // ID unique passé depuis le parent (inclut l'index pour garantir l'unicité)
  expandedSubAudits: Record<string | number, boolean>;
  toggleSubAudit: (entityId: string | number) => void;
  t: (key: string) => string;
}

const SubAudit: React.FC<SubAuditProps> = ({ mainEntity, uniqueId, expandedSubAudits, toggleSubAudit, t }) => {
  const isExpanded = !!expandedSubAudits[uniqueId];
  let parsedDelta: unknown = null;
  // Récupérer le delta depuis mainEntity.delta (priorité) ou logicalUnits[].audits[].delta
  let action: string | undefined = mainEntity.action; // Rétrocompatibilité
  try {
    // D'abord vérifier si le delta est directement dans mainEntity
    if (mainEntity.delta) {
      parsedDelta = JSON.parse(mainEntity.delta);
      // Utiliser l'action de mainEntity si disponible
      if (mainEntity.action) {
        action = mainEntity.action;
      }
    } else {
      // Sinon, chercher le premier audit avec un delta dans les logicalUnits
      const firstAuditWithDelta = mainEntity.logicalUnits?.find((lu) => lu.audits?.some((audit) => audit.delta))?.audits?.find((audit) => audit.delta);

      if (firstAuditWithDelta?.delta) {
        parsedDelta = JSON.parse(firstAuditWithDelta.delta);
        // Utiliser l'action de cet audit si disponible
        if (firstAuditWithDelta.action) {
          action = firstAuditWithDelta.action;
        }
      }
    }
  } catch {
    // Erreur silencieuse : le delta ne sera simplement pas affiché
    // Le delta invalide sera ignoré et ne sera pas affiché
  }
  const style = getAuditStyle(action);
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        if (parsedDelta) {
          toggleSubAudit(uniqueId);
        }
      }}
      className={`group cursor-pointer rounded-lg border ${style.border} ${style.bg} p-3 
                   transition-all duration-200 ease-in-out
                   hover:shadow-xs hover:border-gray-300
                   ${parsedDelta ? "hover:bg-white" : ""}`}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          if (parsedDelta) {
            toggleSubAudit(uniqueId);
          }
        }
      }}
    >
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-gray-900 text-sm">{t(mainEntity.entityName)}</h4>
        <div className="flex items-center gap-3">
          <div
            className={`flex items-center justify-center w-6 h-6 rounded-full ${style.iconBg} 
                           transition-all duration-200 group-hover:scale-105`}
          >
            {style.icon}
          </div>
          {parsedDelta !== null && (
            <div
              className={`transform transition-transform duration-200 ease-in-out text-gray-500
                             ${isExpanded ? "rotate-90" : "rotate-0"} group-hover:text-gray-700`}
            >
              <FaAngleRight className="h-3 w-3" />
            </div>
          )}
        </div>
      </div>
      {isExpanded && parsedDelta !== null && (
        <div className="mt-3 pt-3 border-t border-gray-200 animate-fade-in">
          <pre className="font-mono text-xs text-gray-700 bg-gray-50 rounded-md p-3 overflow-x-auto wrap-break-word whitespace-pre-wrap">
            {typeof parsedDelta === "string" ? parsedDelta : JSON.stringify(parsedDelta, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

/**
 * Composant générique d'affichage des audits pour une entité.
 * @param {string|number} id - Identifiant de l'entité à auditer
 * @param {function} doGetAuditsData - Fonction async (axios, id) => audits
 * @param {string} [toastName] - Nom pour le toast/gestion d'erreur
 */
const U2000AuditPanelComponent: React.FC<U2000AuditPanelComponentProps> = ({ id, doGetAuditsData, toastName = "U2000AuditPanel", height, maxHeight = 300, className }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [audits, setAudits] = useState<AuditType[]>([]);
  const [expandedAuditId, setExpandedAuditId] = useState<string | number | null>(null);
  const [expandedSubAudits, setExpandedSubAudits] = useState<Record<string | number, boolean>>({});
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  const getAuditsData = useCallback(
    async (auditId: string | number) => {
      if (!auditId) {
        setAudits([]);
        return;
      }
      try {
        const response = await doGetAuditsData(u2000AxiosAuthenticated, auditId);
        const results = response.data.results;
        
        // La réponse est toujours un tableau d'AuditType avec mainEntities
        if (Array.isArray(results)) {
          setAudits(results as AuditType[]);
        } else {
          setAudits([]);
        }
      } catch (error) {
        const errorResult = U2000HandleApiError(error as Parameters<typeof U2000HandleApiError>[0], t, toastName);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err: U2000ApiErrorResult["genericErrors"][number]) => {
            if (err?.text && err?.title) {
              toast.error(err.text, err.title);
            }
          });
        }
      }
    },
    [doGetAuditsData, u2000AxiosAuthenticated, t, toastName, toast]
  );

  const toggleAudit = useCallback((requestUuid: string | number, entities: MainEntityType[] | undefined) => {
    if (!entities || !Array.isArray(entities)) {
      return;
    }
    setExpandedAuditId((prevId) => {
      if (prevId === requestUuid) {
        setExpandedSubAudits({});
        return null;
      } else {
        // Créer des identifiants uniques pour chaque entité en combinant entityId, action, delta hash et index
        // Ces IDs doivent correspondre à ceux utilisés dans le rendu pour que l'expansion fonctionne correctement
        const newExpandedSubs: Record<string | number, boolean> = {};
        entities.forEach((entity, index) => {
          const baseId = entity.entityId ?? entity.id ?? entity.entityName;
          const action = entity.action ?? 'unknown';
          const deltaHash = entity.delta ? entity.delta.substring(0, 30).replace(/[^a-zA-Z0-9]/g, '') : '';
          const uniqueId = `${baseId}-${action}-${deltaHash}-${index}`;
          newExpandedSubs[uniqueId] = true;
        });
        setExpandedSubAudits(newExpandedSubs);
        return requestUuid;
      }
    });
  }, []);

  const toggleSubAudit = useCallback((entityId: string | number) => {
    setExpandedSubAudits((prev) => ({
      ...prev,
      [entityId]: !prev[entityId],
    }));
  }, []);

  useEffect(() => {
    getAuditsData(id);
    setExpandedAuditId(null);
    setExpandedSubAudits({});
  }, [id, getAuditsData]);

  // Fonction pour extraire toutes les entités à afficher (mainEntities + audits dans logicalUnits)
  // Cette fonction extrait toutes les entités qui doivent être affichées comme des SubAudit séparés
  const getAllEntitiesToDisplay = useCallback((result: AuditType): MainEntityType[] => {
    const mainEntities = result.mainEntities || result.audits || [];
    const allEntities: MainEntityType[] = [];
    
    mainEntities.forEach((mainEntity) => {
      // Ajouter le mainEntity lui-même s'il a un delta ou une action directe
      if (mainEntity.delta || mainEntity.action) {
        allEntities.push(mainEntity);
      }
      
      // Extraire tous les audits des logicalUnits comme des entités séparées
      // Cela permet d'afficher country et state comme des entités distinctes
      if (mainEntity.logicalUnits && Array.isArray(mainEntity.logicalUnits)) {
        mainEntity.logicalUnits.forEach((logicalUnit) => {
          if (logicalUnit.audits && Array.isArray(logicalUnit.audits)) {
            logicalUnit.audits.forEach((audit) => {
              // Créer un MainEntityType à partir de l'audit du logicalUnit pour l'affichage
              allEntities.push({
                entityId: audit.entityId,
                entityName: audit.entityName,
                action: audit.action,
                delta: audit.delta,
                parentEntityId: audit.parentEntityId,
              });
            });
          }
        });
      }
    });
    
    return allEntities;
  }, []);

  // Build container classes and inline styles. Tailwind classes must be literal
  // in source for JIT; if user passed a Tailwind class (like 'h-64' or 'max-h-40')
  // we use it, otherwise we fall back to inline styles for dynamic values.
  const { containerStyle, containerClass } = useMemo(() => {
    const style: React.CSSProperties = {};
    let heightClass = "";
    let maxHeightClass = "";

    if (height !== undefined && height !== null) {
      if (typeof height === "number") {
        style.height = `${height}px`;
      } else if (typeof height === "string") {
        const v = height.trim();
        if (v.startsWith("h-") || v.startsWith("h:") || v.startsWith('h"')) {
          heightClass = v;
        } else if (/^\d+$/.test(v)) {
          style.height = `${v}px`;
        } else {
          // assume unit provided like '30rem' or '300px' or an arbitrary value
          style.height = v;
        }
      }
    }

    if (maxHeight !== undefined && maxHeight !== null) {
      if (typeof maxHeight === "number") {
        style.maxHeight = `${maxHeight}px`;
      } else if (typeof maxHeight === "string") {
        const v = maxHeight.trim();
        if (v.startsWith("max-h-")) {
          maxHeightClass = v;
        } else if (/^\d+$/.test(v)) {
          style.maxHeight = `${v}px`;
        } else {
          style.maxHeight = v;
        }
      }
    }

    const shouldScroll = Boolean(height !== undefined && height !== null) || Boolean(maxHeight !== undefined && maxHeight !== null);
    const classString = ["flex", "flex-col", "space-y-2", heightClass, maxHeightClass, shouldScroll ? "overflow-y-auto" : "", className ?? ""].filter(Boolean).join(" ");

    return { containerStyle: style, containerClass: classString };
  }, [height, maxHeight, className]);

  if (!audits || audits.length === 0) {
    return null;
  }

  return (
    <div className={containerClass} style={containerStyle}>
      {audits.map((result) => {
        const allEntitiesToDisplay = getAllEntitiesToDisplay(result);
        const panelStyle = getAuditPanelStyle(allEntitiesToDisplay);
        const requestUuid = result.requestUuid ?? result.id ?? result.createdAt ?? "";
        const isExpanded = expandedAuditId === requestUuid;
        return (
          <div
            key={requestUuid}
            onClick={() => toggleAudit(requestUuid, allEntitiesToDisplay)}
            className={`group cursor-pointer rounded-lg border ${panelStyle.border} ${panelStyle.bg} ${panelStyle.accent}
                       p-4 text-sm transition-all duration-200 ease-in-out
                       hover:shadow-md hover:bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500/20`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggleAudit(requestUuid, allEntitiesToDisplay);
              }
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <h3 className="font-semibold text-gray-900">{U2000FormatDateTime(result.createdAt)}</h3>
                <p className="text-sm text-gray-600 mt-1">{result.username}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 font-medium">
                  {allEntitiesToDisplay.length} {allEntitiesToDisplay.length === 1 ? "modification" : "modifications"}
                </span>
                <div
                  className={`transform transition-transform duration-200 ease-in-out text-gray-500
                               ${isExpanded ? "rotate-90" : "rotate-0"} group-hover:text-gray-700`}
                >
                  <FaAngleRight className="h-4 w-4" />
                </div>
              </div>
            </div>
            {isExpanded && (
              <div className="mt-4 space-y-3 animate-fade-in">
                {allEntitiesToDisplay.map((entity, index) => {
                  // Créer une clé unique en combinant entityId, action, delta hash et index pour éviter les conflits
                  // Cet ID doit correspondre exactement à celui utilisé dans toggleAudit
                  const baseId = entity.entityId ?? entity.id ?? entity.entityName;
                  const action = entity.action ?? 'unknown';
                  const deltaHash = entity.delta ? entity.delta.substring(0, 30).replace(/[^a-zA-Z0-9]/g, '') : '';
                  const uniqueId = `${baseId}-${action}-${deltaHash}-${index}`;
                  return (
                    <SubAudit
                      key={uniqueId}
                      mainEntity={entity}
                      uniqueId={uniqueId}
                      expandedSubAudits={expandedSubAudits}
                      toggleSubAudit={toggleSubAudit}
                      t={t}
                    />
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default U2000AuditPanelComponent;
